import classNames from 'classnames';
import hoistNonReactStatics from 'hoist-non-react-statics';
import type { UploadProps as RcUploadProps } from 'rc-upload';
import RcUpload from 'rc-upload';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import React from 'react';
import { flushSync } from 'react-dom';
import FilePreview from '../file-preview';
import { useFormSettingsContext } from '../form/Form.context';
import Dragger from './dragger';
import { UploadSC, prefixCls } from './upload.styled';
import { RcFile, UploadChangeParam, UploadFile, UploadHandle, UploadProps } from './upload.types';
import {
  onPreview as defaultPreview,
  file2Obj,
  getFileItem,
  getThumbnailUrl,
  normFile,
  removeFileItem,
  updateFileList,
} from './upload.utils';
import UploadFilePreview from './components/upload-file-preview';

export const LIST_IGNORE = `__LIST_IGNORE_${Date.now()}__`;

type TComp = React.ForwardRefExoticComponent<
  UploadProps<unknown> & React.RefAttributes<UploadHandle>
> & {
  LIST_IGNORE: typeof LIST_IGNORE;
  Dragger: typeof Dragger;
  getValueFromEvent: typeof normFile;
  valuePropName: string;
};

/**
 * Upload component for hilbi.
 * inspired by https://github.com/ant-design/ant-design/tree/master/components/upload
 */
const Upload = <T extends any>(
  props: UploadProps<T>,
  ref?: React.Ref<UploadHandle>,
): JSX.Element => {
  const {
    children,
    className,
    disabled,
    defaultFileList,
    maxCount,
    onChange,
    accept,
    onRemove,
    data,
    multiple,
    action,
    showFileList,
    fileList,
    onPreview,
    onDrop,
    style,
    error,
  } = props;
  const settings = useFormSettingsContext({ disabled });
  const upload = React.useRef<RcUpload>(null);
  const [dragState, setDragState] = React.useState<string>('drop');

  const [mergedFileList, setMergedFileList] = useMergedState(defaultFileList || [], {
    value: fileList,
    postState: (list) => list ?? [],
  });

  React.useImperativeHandle(ref, () => ({
    upload: upload.current,
  }));

  const onInternalChange = (
    file: UploadFile,
    changedFileList: UploadFile[],
    event?: { percent: number },
  ) => {
    let cloneList = [...changedFileList];

    // Cut to match count
    if (maxCount === 1) {
      cloneList = cloneList.slice(-1);
    } else if (maxCount) {
      cloneList = cloneList.slice(0, maxCount);
    }

    // Prevent React18 auto batch since input[upload] trigger process at same time
    // which makes mergedFileList closure problem
    flushSync(() => {
      setMergedFileList(cloneList);
    });

    const changeInfo: UploadChangeParam<UploadFile> = {
      file: file as UploadFile,
      fileList: cloneList,
    };

    if (event) {
      changeInfo.event = event;
    }

    onChange?.(changeInfo);
  };

  const mergedBeforeUpload = async (file: RcFile, fileListArgs: RcFile[]) => {
    const { beforeUpload } = props;

    let parsedFile: File | Blob | string = file;
    if (beforeUpload) {
      const result = await beforeUpload(file, fileListArgs);

      if (result === false) {
        return false;
      }

      // Hack for LIST_IGNORE, we add additional info to remove from the list
      delete (file as any)[LIST_IGNORE];
      if ((result as any) === LIST_IGNORE) {
        Object.defineProperty(file, LIST_IGNORE, {
          value: true,
          configurable: true,
        });
        return false;
      }

      if (typeof result === 'object' && result) {
        parsedFile = result as File;
      }
    }

    return parsedFile as RcFile;
  };

  const onBatchStart: RcUploadProps['onBatchStart'] = (batchFileInfoList) => {
    // Skip file which marked as `LIST_IGNORE`, these file will not add to file list
    const filteredFileInfoList = batchFileInfoList.filter(
      (info) => !(info.file as any)[LIST_IGNORE],
    );

    // Nothing to do since no file need upload
    if (!filteredFileInfoList.length) {
      return;
    }

    const objectFileList = filteredFileInfoList.map((info) => file2Obj(info.file as RcFile));

    // Concat new files with prev files
    let newFileList = [...mergedFileList];

    objectFileList.forEach((fileObj) => {
      // Replace file if exist
      newFileList = updateFileList(fileObj, newFileList);
    });

    objectFileList.forEach((fileObj, index) => {
      // Repeat trigger `onChange` event for compatible
      let triggerFileObj: UploadFile = fileObj;

      if (!filteredFileInfoList[index].parsedFile) {
        // `beforeUpload` return false
        const { originFileObj } = fileObj;
        let clone;

        try {
          clone = new File([originFileObj], originFileObj.name, {
            type: originFileObj.type,
          }) as any as UploadFile;
        } catch (e) {
          clone = new Blob([originFileObj], {
            type: originFileObj.type,
          }) as any as UploadFile;
          clone.name = originFileObj.name;
          clone.lastModifiedDate = new Date();
          clone.lastModified = new Date().getTime();
        }

        clone.uid = fileObj.uid;
        triggerFileObj = clone;
      } else {
        // Inject `uploading` status
        fileObj.status = 'uploading';
      }

      onInternalChange(triggerFileObj, newFileList);
    });
  };

  const onSuccess = (response: any, file: RcFile, xhr: any) => {
    try {
      if (typeof response === 'string') {
        response = JSON.parse(response);
      }
    } catch (e) {
      /* do nothing */
    }

    // removed
    if (!getFileItem(file, mergedFileList)) {
      return;
    }

    const targetItem = file2Obj(file);
    targetItem.status = 'done';
    targetItem.percent = 100;
    targetItem.response = response;
    targetItem.xhr = xhr;

    const nextFileList = updateFileList(targetItem, mergedFileList);

    onInternalChange(targetItem, nextFileList);
  };

  const onProgress = (e: { percent: number }, file: RcFile) => {
    // removed
    if (!getFileItem(file, mergedFileList)) {
      return;
    }

    const targetItem = file2Obj(file);
    targetItem.status = 'uploading';
    targetItem.percent = e.percent;

    const nextFileList = updateFileList(targetItem, mergedFileList);

    onInternalChange(targetItem, nextFileList, e);
  };

  const onError = (error: Error, response: any, file: RcFile) => {
    // removed
    if (!getFileItem(file, mergedFileList)) {
      return;
    }

    const targetItem = file2Obj(file);
    targetItem.error = error;
    targetItem.response = response;
    targetItem.status = 'error';

    const nextFileList = updateFileList(targetItem, mergedFileList);

    onInternalChange(targetItem, nextFileList);
  };

  const handleRemove = (file: UploadFile) => {
    let currentFile: UploadFile;
    Promise.resolve(typeof onRemove === 'function' ? onRemove(file) : onRemove).then((ret) => {
      // Prevent removing file
      if (ret === false) {
        return;
      }

      const removedFileList = removeFileItem(file, mergedFileList);

      if (removedFileList) {
        currentFile = { ...file, status: 'removed' };
        mergedFileList?.forEach((item) => {
          const matchKey = currentFile.uid !== undefined ? 'uid' : 'name';
          if (item[matchKey] === currentFile[matchKey] && !Object.isFrozen(item)) {
            item.status = 'removed';
          }
        });
        upload.current?.abort(currentFile as RcFile);

        onInternalChange(currentFile, removedFileList);
      }
    });
  };

  const onFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    setDragState(e.type);

    if (e.type === 'drop') {
      onDrop?.(e);
    }
  };

  const previewFn = onPreview ?? defaultPreview;

  const renderUploadList = () => {
    if (!showFileList || mergedFileList.length === 0) {
      return null;
    }

    return (
      <FilePreview.List style={{ marginTop: 16 }}>
        {mergedFileList.map((f) => (
          <UploadFilePreview file={f} onDelete={handleRemove} onPreview={previewFn} key={f.uid} />
        ))}
      </FilePreview.List>
    );
  };

  const rcUploadProps = {
    onBatchStart,
    onError,
    onProgress,
    onSuccess,
    ...props,
    data,
    multiple,
    action,
    accept,
    disabled: settings.disabled,
    beforeUpload: mergedBeforeUpload,
    onChange: undefined,
  } as RcUploadProps;

  let content;

  if (props.type === 'drag') {
    const dragCls = classNames(`${prefixCls}-wrapper`, {
      [`${prefixCls}-drag`]: !settings.disabled,
      [`${prefixCls}-drag-uploading`]: mergedFileList.some((file) => file.status === 'uploading'),
      [`${prefixCls}-drag-hover`]: dragState === 'dragover',
    });

    content = (
      <div
        className={dragCls}
        onDrop={onFileDrop}
        onDragOver={onFileDrop}
        onDragLeave={onFileDrop}
        style={style}
      >
        <RcUpload {...rcUploadProps} ref={upload}>
          {children}
        </RcUpload>
      </div>
    );
  } else {
    content = (
      <RcUpload {...rcUploadProps} ref={upload}>
        {children}
      </RcUpload>
    );
  }

  return (
    <UploadSC
      className={classNames(prefixCls, className, {
        ['ui-upload-disabled']: rcUploadProps.disabled,
        ['ui-upload-error']: error,
      })}
      style={style}
    >
      {content}
      {renderUploadList()}
    </UploadSC>
  );
};

Upload.LIST_IGNORE = LIST_IGNORE;
Upload.Dragger = Dragger;
Upload.getValueFromEvent = normFile;
Upload.valuePropName = 'fileList';

export default hoistNonReactStatics(React.forwardRef(Upload), Upload) as TComp;
